import "@/App.css";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import lazyPage from "@/lib/lazyPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { VersionWatch } from "@/components/VersionWatch";
import { AccountOnHoldNotice } from "@/components/AccountOnHold";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { NotFoundPage } from "@/components/JoyFeatures";
import { useSiteLook } from "@/lib/useSiteLook";
import BrandBar from "@/components/BrandBar";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
const ForgotPasswordPage = lazyPage(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyPage(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazyPage(() => import("@/pages/VerifyEmailPage"));
const InvitePage = lazyPage(() => import("@/pages/InvitePage"));
const FrameStillsPayPage = lazyPage(() => import("@/pages/FrameStillsPayPage"));
import DashboardPage from "@/pages/DashboardPage";
const GemBagPage = lazyPage(() => import("@/pages/GemBagPage"));
const ProfilePage = lazyPage(() => import("@/pages/ProfilePage"));
const AdminProductsPage = lazyPage(() => import("@/pages/AdminProductsPage"));
const AdminUsersPage = lazyPage(() => import("@/pages/AdminUsersPage"));
const AdminSettingsPage = lazyPage(() => import("@/pages/AdminSettingsPage"));
const AdminPrepStatusPage = lazyPage(() => import("@/pages/AdminPrepStatusPage"));
const AdminAnnouncementPreviewPage = lazyPage(() => import("@/pages/AdminAnnouncementPreviewPage"));
const AdminAssistantPage = lazyPage(() => import("@/pages/AdminAssistantPage"));
const AdminRedemptionsPage = lazyPage(() => import("@/pages/AdminRedemptionsPage"));
const ClientDetailPage = lazyPage(() => import("@/pages/ClientDetailPage"));
const AdminAnalyticsPage = lazyPage(() => import("@/pages/AdminAnalyticsPage"));
const AdminPaymentsPage = lazyPage(() => import("@/pages/AdminPaymentsPage"));
const AdminChatsPage = lazyPage(() => import("@/pages/AdminChatsPage"));
const AdminAutoAdvancePage = lazyPage(() => import("@/pages/AdminAutoAdvancePage"));
const AdminProjectTagsPage = lazyPage(() => import("@/pages/AdminProjectTagsPage"));
const AdminSongDeadlinePage = lazyPage(() => import("@/pages/AdminSongDeadlinePage"));
const AdminDriveAccessPage = lazyPage(() => import("@/pages/AdminDriveAccessPage"));
const AdminEditorTimePage = lazyPage(() => import("@/pages/AdminEditorTimePage"));
const AdminTrainingPage = lazyPage(() => import("@/pages/AdminTrainingPage"));
const AdminTrainingBlindPage = lazyPage(() => import("@/pages/AdminTrainingBlindPage"));
const EditorChatsPage = lazyPage(() => import("@/pages/EditorChatsPage"));
const EditorPaymentsPage = lazyPage(() => import("@/pages/EditorPaymentsPage"));
const EditorStatsPage = lazyPage(() => import("@/pages/EditorStatsPage"));
const EditorSettingsPage = lazyPage(() => import("@/pages/EditorSettingsPage"));
const EditorGuidePage = lazyPage(() => import("@/pages/EditorGuidePage"));
const EditorRedemptionsPage = lazyPage(() => import("@/pages/EditorRedemptionsPage"));
const EditorReferencePage = lazyPage(() => import("@/pages/EditorReferencePage"));
const RedemptionShareCompPreview = lazyPage(() => import("@/pages/RedemptionShareCompPreview"));
const RedemptionSharePage = lazyPage(() => import("@/pages/RedemptionSharePage"));
const ClientChatsPage = lazyPage(() => import("@/pages/ClientChatsPage"));
const StyleProfilePage = lazyPage(() => import("@/pages/StyleProfilePage"));
const NewOrderPage = lazyPage(() => import("@/pages/NewOrderPage"));
const TermsPage = lazyPage(() => import("@/pages/TermsPage"));
const PrivacyPage = lazyPage(() => import("@/pages/PrivacyPage"));
const OrderCompletePage = lazyPage(() => import("@/pages/OrderCompletePage"));
const HelpPage = lazyPage(() => import("@/pages/HelpPage"));
const ClientPricingPage = lazyPage(() => import("@/pages/ClientPricingPage"));

/* Route-level code splitting (2026-09-11): every page except Login and the projects board is
   its own chunk, loaded on first navigation, via lazyPage (which also reloads ONCE on a stale
   chunk after a deploy). The fallback is the same blank obsidian screen the auth gate shows. */
const PageFallback = () => <div className="min-h-screen bg-obsidian" />;

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-obsidian flex items-center justify-center"><div className="text-gold-leaf font-mono text-sm animate-pulse">Loading...</div></div>;
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // MUST STAY ABOVE THE EARLY RETURNS BELOW. /terms, /privacy and the share page return before the <Routes>
  // table, so a hook under them would run on some paths and not others and React would fail with "rendered
  // fewer hooks than expected" as soon as the path changed. A NEW EARLY RETURN GOES BELOW THIS LINE.
  // At app level rather than in Layout so the routes that render outside Layout are covered too -- chiefly
  // /order-complete, the page a client lands on straight after paying. See lib/useSiteLook.js and 0bd.
  useSiteLook(user?.role);

  // Public pages — always accessible, even during auth loading
  const path = window.location.pathname;
  if (path === '/terms') return <Suspense fallback={<PageFallback />}><TermsPage /></Suspense>;
  if (path === '/privacy') return <Suspense fallback={<PageFallback />}><PrivacyPage /></Suspense>;
  // Public, token-gated, auth-independent — must render BEFORE auth resolution.
  // Mirrors /terms and /privacy bail-early pattern; share page must not flash
  // the auth-loading screen and must not depend on AuthContext state.
  if (path.startsWith('/share/redemption/')) {
    const shareToken = path.split('/share/redemption/')[1] || '';
    // Strip any trailing slash or query bits — we pass the token straight in.
    const cleanToken = shareToken.replace(/\/.*$/, '');
    return <Suspense fallback={<PageFallback />}><RedemptionSharePage key={cleanToken} propToken={cleanToken} /></Suspense>;
  }

  if (loading) return <div className="min-h-screen bg-obsidian" />;

  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/pay/frame-stills/:token" element={<FrameStillsPayPage />} />
      <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/archived" element={<ProtectedRoute><Layout><DashboardPage archived /></Layout></ProtectedRoute>} />
      <Route path="/trash" element={<ProtectedRoute><Layout><DashboardPage trashed /></Layout></ProtectedRoute>} />
      <Route path="/gem-bag" element={<ProtectedRoute allowedRoles={["client"]}><Layout><GemBagPage /></Layout></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute allowedRoles={["client"]}><Layout><ClientPricingPage /></Layout></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminProductsPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Layout><AdminUsersPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/assistant" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminAssistantPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminSettingsPage /></Layout></ProtectedRoute>} />
      {/* full screen, no sidebar: left open beside the prep computer (pages/AdminPrepStatusPage.js) */}
      <Route path="/admin/prep-status" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><AdminPrepStatusPage /></ProtectedRoute>} />
      <Route path="/admin/announcements/preview/:key" element={<ProtectedRoute allowedRoles={["admin"]}><AdminAnnouncementPreviewPage /></ProtectedRoute>} />
      <Route path="/admin/redemptions" element={<ProtectedRoute allowedRoles={["admin","staff"]}><Layout><AdminRedemptionsPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Layout><ClientDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminAnalyticsPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute allowedRoles={["admin", "billing", "staff"]}><Layout><AdminPaymentsPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/chats" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Layout><AdminChatsPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/song-deadline" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Layout><AdminSongDeadlinePage /></Layout></ProtectedRoute>} />
      <Route path="/admin/auto-advance" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Layout><AdminAutoAdvancePage /></Layout></ProtectedRoute>} />
      <Route path="/admin/project-tags" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminProjectTagsPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/drive-access" element={<ProtectedRoute allowedRoles={["admin", "staff"]}><Layout><AdminDriveAccessPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/editor-time" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminEditorTimePage /></Layout></ProtectedRoute>} />
      {/* the AI training, run by itself; what it needs from Ryan (backend app/training_autopilot.py) */}
      <Route path="/admin/training" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminTrainingPage /></Layout></ProtectedRoute>} />
      <Route path="/admin/training/blind" element={<ProtectedRoute allowedRoles={["admin"]}><Layout><AdminTrainingBlindPage /></Layout></ProtectedRoute>} />
      <Route path="/editor/chats" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorChatsPage /></Layout></ProtectedRoute>} />
      <Route path="/editor/payments" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorPaymentsPage /></Layout></ProtectedRoute>} />
      <Route path="/editor/stats" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorStatsPage /></Layout></ProtectedRoute>} />
      <Route path="/editor/settings" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorSettingsPage /></Layout></ProtectedRoute>} />
      <Route path="/editor/guide" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorGuidePage /></Layout></ProtectedRoute>} />
      <Route path="/editor/redemptions" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorRedemptionsPage /></Layout></ProtectedRoute>} />
      <Route path="/editor/reference" element={<ProtectedRoute allowedRoles={["editor"]}><Layout><EditorReferencePage /></Layout></ProtectedRoute>} />
      <Route path="/design-preview/redemption-share" element={<ProtectedRoute allowedRoles={["admin"]}><RedemptionShareCompPreview /></ProtectedRoute>} />
      <Route path="/client/chats" element={<ProtectedRoute allowedRoles={["client"]}><Layout><ClientChatsPage /></Layout></ProtectedRoute>} />
      <Route path="/style-profile" element={<ProtectedRoute><Layout><StyleProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/new-order" element={<ProtectedRoute allowedRoles={["client", "admin", "staff"]}><Layout><NewOrderPage /></Layout></ProtectedRoute>} />
      <Route path="/order-complete/:projectId" element={<ProtectedRoute allowedRoles={["client"]}><OrderCompletePage /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/help" element={<ProtectedRoute allowedRoles={["client"]}><Layout><HelpPage /></Layout></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  // Detect share-page route at root so we can skip BrandBar + portal chrome
  // entirely. The share microsite must be a clean surface — no portal branding
  // leakage, no cross-promo links to RoughCut / Pixally / CrowdReel, no theme
  // toggle. This mirrors the public-page bail-early pattern in AppRoutes.
  const isSharePage = typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/share/redemption/');
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <ConfirmProvider>
          {!isSharePage && <BrandBar />}
          <div style={{ paddingTop: isSharePage ? '0' : '32px' }}>
            <AppRoutes />
          </div>
          <Toaster position="top-right" richColors />
          {!isSharePage && <VersionWatch />}
          <AccountOnHoldNotice />
        </ConfirmProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
